import type { InvitationPreviewDto } from "@bafe/api-client";
import { Button, Field, Input } from "@bafe/ui";
import { checkAccountPassword } from "@bafe/validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { AuthShell } from "../../components/AuthShell";
import { ErrorPanel } from "../../components/ErrorPanel";
import { PasswordField } from "../../components/PasswordField";
import { api } from "../../lib/api";
import { useCompleteSignIn } from "../../lib/auth-flow";
import { fieldError } from "../../lib/errors";
import { ROLE_LABEL } from "../../lib/status";

export function AcceptInvitation() {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	const preview = useQuery({
		queryKey: ["invitation", token],
		queryFn: () => api.previewInvitation(token ?? ""),
		enabled: token !== null && token.length >= 16,
		retry: false,
	});

	if (token === null || token.length < 16) {
		return (
			<AuthShell title="This invitation link is incomplete">
				<p className="text-sm text-(--text-secondary)">
					Open the link from your email exactly as it was sent, or ask for a new invitation.
				</p>
			</AuthShell>
		);
	}

	if (preview.isPending) {
		return (
			<AuthShell title="Opening your invitation">
				<p className="text-sm text-(--text-secondary)">Checking the invitation.</p>
			</AuthShell>
		);
	}

	if (preview.isError) {
		return (
			<AuthShell title="This invitation cannot be used">
				<ErrorPanel error={preview.error} title="We could not open that invitation." />
			</AuthShell>
		);
	}

	return <AcceptForm token={token} preview={preview.data} />;
}

function AcceptForm({ token, preview }: { token: string; preview: InvitationPreviewDto }) {
	const completeSignIn = useCompleteSignIn();
	const [fullName, setFullName] = useState("");
	const [password, setPassword] = useState("");
	const [touchedPassword, setTouchedPassword] = useState(false);

	const passwordCheck = checkAccountPassword(password);
	const passwordError = touchedPassword && !passwordCheck.ok ? (passwordCheck.message ?? null) : null;
	const role = ROLE_LABEL[preview.role];

	const accept = useMutation({
		mutationFn: () =>
			api.acceptInvitation(
				preview.accountExists ? { token } : { token, fullName: fullName.trim(), password },
			),
		onSuccess: (session) => completeSignIn(session, "/"),
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!preview.accountExists) {
			setTouchedPassword(true);
			if (fullName.trim().length === 0 || !passwordCheck.ok) return;
		}
		accept.mutate();
	}

	return (
		<AuthShell
			title={`Join ${preview.organisationName}`}
			description={
				preview.accountExists
					? `Sign in as ${preview.email} to join as ${role}. If you are already signed in with that address, continue below.`
					: `Create an account for ${preview.email} to join as ${role}.`
			}
			footer={
				preview.accountExists ? (
					<Link
						to={`/signin?next=${encodeURIComponent(`/invite?token=${token}`)}`}
						className="font-medium text-(--color-brand-700) underline underline-offset-2"
					>
						Sign in as {preview.email}
					</Link>
				) : (
					<>
						Already have an account?{" "}
						<Link to="/signin" className="font-medium text-(--color-brand-700) underline underline-offset-2">
							Sign in
						</Link>
					</>
				)
			}
		>
			<form className="flex flex-col gap-4" onSubmit={submit} noValidate>
				{accept.isError && <ErrorPanel error={accept.error} title="We could not join that workspace." />}

				{preview.accountExists ? null : (
					<>
						<Field label="Your name" error={fieldError(accept.error, "fullName")} required>
							{({ id, describedBy, invalid }) => (
								<Input
									id={id}
									value={fullName}
									required
									autoComplete="name"
									aria-describedby={describedBy}
									aria-invalid={invalid}
									onChange={(event) => setFullName(event.target.value)}
								/>
							)}
						</Field>
						<PasswordField
							label="Password"
							value={password}
							onChange={(value) => {
								setPassword(value);
								setTouchedPassword(true);
							}}
							error={passwordError}
							help="At least 10 characters. Longer is better than complicated."
							required
						/>
					</>
				)}

				<Button type="submit" variant="primary" fullWidth pending={accept.isPending}>
					{preview.accountExists ? "Join workspace" : "Create account"}
				</Button>
			</form>
		</AuthShell>
	);
}
